/** Traditional textile and ornament motifs as charts, one character per stitch (`X` = stitch in the contrast color). */
export const KNITTING_CHARTS = {
	/** Norwegian eight-petal Selbu star. */
	selbuStar: [
		'.......X.......',
		'......XXX......',
		'.X...XXXXX...X.',
		'.XX..XXXXX..XX.',
		'.XXX.XXXXX.XXX.',
		'.XXXX.XXX.XXXX.',
		'..XXXX.X.XXXX..',
		'XXXXXXX.XXXXXXX',
		'..XXXX.X.XXXX..',
		'.XXXX.XXX.XXXX.',
		'.XXX.XXXXX.XXX.',
		'.XX..XXXXX..XX.',
		'.X...XXXXX...X.',
		'......XXX......',
		'.......X.......',
	],
	/** Estonian kirivöö (patterned belt) diamond: nested diamonds around a dotted center. */
	kirivooDiamond: [
		'.......X.......',
		'......X.X......',
		'.....X...X.....',
		'....X..X..X....',
		'...X..X.X..X...',
		'..X..X.X.X..X..',
		'.X..X.....X..X.',
		'X..X.X.X.X.X..X',
		'.X..X.....X..X.',
		'..X..X.X.X..X..',
		'...X..X.X..X...',
		'....X..X..X....',
		'.....X...X.....',
		'......X.X......',
		'.......X.......',
	],
	/** Estonian Muhu-style stepped cross. */
	muhuCross: [
		'......XXX......',
		'......XXX......',
		'....X.XXX.X....',
		'...XXXXXXXXX...',
		'..XXX.XXX.XXX..',
		'...X..XXX..X...',
		'XXXXXXXXXXXXXXX',
		'XXXXXXX.XXXXXXX',
		'XXXXXXXXXXXXXXX',
		'...X..XXX..X...',
		'..XXX.XXX.XXX..',
		'...XXXXXXXXX...',
		'....X.XXX.X....',
		'......XXX......',
		'......XXX......',
	],
	/** Celtic looped-square knot, its four strands alternating over and under at the corners. */
	celticKnot: [
		'..XX.......XX..',
		'.X..X.....X..X.',
		'X...X.....X...X',
		'X.........X...X',
		'.XXXXXXXX.X.XX.',
		'..........X....',
		'....X.....X....',
		'....X.....X....',
		'....X.....X....',
		'....X..........',
		'.XX.X.XXXXXXXX.',
		'X...X.........X',
		'X...X.....X...X',
		'.X..X.....X..X.',
		'..XX.......XX..',
	],
	/** Greek key (meander) spiral. */
	greekKey: [
		'...............',
		'.XXXXXXXXXXXXX.',
		'.X...........X.',
		'.X.XXXXXXXXX.X.',
		'.X.X.......X.X.',
		'.X.X.XXXXX.X.X.',
		'.X.X.X...X.X.X.',
		'.X.X.X.X.X.X.X.',
		'.X.X.X.X.X.X.X.',
		'.X.X.X.XXX.X.X.',
		'.X.X.X.....X.X.',
		'.X.X.XXXXXXX.X.',
		'.X.X.........X.',
		'.X.XXXXXXXXXXX.',
		'...............',
	],
	/** Scottish Fair Isle "OXO": an X crossed through a nested O. */
	fairIsleOxo: [
		'X.............X',
		'.X...........X.',
		'..X.........X..',
		'...X...X...X...',
		'....X.X.X.X....',
		'.....X.X.X.....',
		'....X.X.X.X....',
		'...X.X.X.X.X...',
		'....X.X.X.X....',
		'.....X.X.X.....',
		'....X.X.X.X....',
		'...X...X...X...',
		'..X.........X..',
		'.X...........X.',
		'X.............X',
	],
	/** Moroccan khatam: eight-pointed star of two overlapping squares, around a small diamond. */
	moroccanStar: [
		'.......X.......',
		'......X.X......',
		'..XXXX...XXXX..',
		'..X.........X..',
		'..X.........X..',
		'..X....X....X..',
		'.X....X.X....X.',
		'X....X.X.X....X',
		'.X....X.X....X.',
		'..X....X....X..',
		'..X.........X..',
		'..X.........X..',
		'..XXXX...XXXX..',
		'......X.X......',
		'.......X.......',
	],
	/** Andean chakana: stepped cross with an open center. */
	andeanChakana: [
		'.....XXXXX.....',
		'.....XXXXX.....',
		'...XXXXXXXXX...',
		'..XXXXXXXXXXX..',
		'..XXXXXXXXXXX..',
		'XXXXXXX.XXXXXXX',
		'XXXXXX...XXXXXX',
		'XXXXX.....XXXXX',
		'XXXXXX...XXXXXX',
		'XXXXXXX.XXXXXXX',
		'..XXXXXXXXXXX..',
		'..XXXXXXXXXXX..',
		'...XXXXXXXXX...',
		'.....XXXXX.....',
		'.....XXXXX.....',
	],
	/** Navajo-style stepped diamond (eye dazzler): nested stair-stepped diamonds. */
	navajoDiamond: [
		'.......X.......',
		'......XXX......',
		'.....XX.XX.....',
		'....XX.X.XX....',
		'...XX.XXX.XX...',
		'..XX.XX.XX.XX..',
		'.XX.XX...XX.XX.',
		'XX.XX..X..XX.XX',
		'.XX.XX...XX.XX.',
		'..XX.XX.XX.XX..',
		'...XX.XXX.XX...',
		'....XX.X.XX....',
		'.....XX.XX.....',
		'......XXX......',
		'.......X.......',
	],
};

export type KnittingChartName = keyof typeof KNITTING_CHARTS;

const STITCH_PX = 2;
/** Tile edge in stitches: the motif plus a gap, with a single "louse" stitch at the tile corner. */
const TILE_STITCHES = 20;
export const KNITTING_TILE_PX = TILE_STITCHES * STITCH_PX;

/** A tileable pattern of the given chart in the given CSS color, as a `url(...)` for `background-image`. */
export function knittingPatternUrl(chartName: KnittingChartName, color: string): string {
	const chart = KNITTING_CHARTS[chartName];
	const offset = (TILE_STITCHES - chart.length) / 2;
	const rects: string[] = [];
	chart.forEach((row, y) => {
		row.split('').forEach((stitch, x) => {
			if (stitch === 'X') rects.push(`<rect x="${(x + offset) * STITCH_PX}" y="${(y + offset) * STITCH_PX}" width="${STITCH_PX}" height="${STITCH_PX}"/>`);
		});
	});
	rects.push(`<rect x="0" y="0" width="${STITCH_PX}" height="${STITCH_PX}"/>`);
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${KNITTING_TILE_PX}" height="${KNITTING_TILE_PX}" fill="${color}" shape-rendering="crispEdges">${rects.join('')}</svg>`;
	return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
